// src/pages/ChatWithAI/utils/chatAuthHelper.js
/**
 * ✅ Helper isolado para autenticação no Chat com IA
 * Suporta FETCH e AXIOS com retry automático
 * NÃO AFETA outras partes do sistema
 */

import axios from 'axios';

let isRefreshing = false;
let refreshPromise = null;

/**
 * Renovar token usando AuthContext (sem modificá-lo)
 */
async function refreshTokenForChat(refreshAccessToken) {
  if (isRefreshing && refreshPromise) {
    console.log('🔄 Chat Auth: Aguardando refresh em andamento...');
    return refreshPromise;
  }

  isRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      console.log('🔐 Chat Auth: Renovando token...');
      
      const success = await refreshAccessToken();
      
      if (success) {
        console.log('✅ Chat Auth: Token renovado com sucesso');
        return true;
      } else {
        console.error('❌ Chat Auth: Falha ao renovar token');
        return false;
      }
      
    } catch (error) {
      console.error('❌ Chat Auth: Erro ao renovar token:', error);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * ✅ Fetch com retry automático (para streaming)
 */
export async function chatFetchWithAuth(url, options, refreshAccessToken, maxRetries = 1) {
  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        ...options.headers,
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log(`📤 Chat Fetch (attempt ${attempt + 1}/${maxRetries + 1}):`, {
        url,
        hasToken: !!token
      });

      const response = await fetch(url, {
        ...options,
        headers
      });

      if (response.ok || (response.status !== 401 && response.status !== 403)) {
        return response;
      }

      if (response.status === 401 || response.status === 403) {
        console.warn('⚠️ Chat Auth: Token expirado, tentando renovar...');
        
        if (attempt >= maxRetries) {
          console.error('❌ Chat Auth: Máximo de tentativas atingido');
          return response;
        }

        const refreshed = await refreshTokenForChat(refreshAccessToken);
        
        if (!refreshed) {
          console.error('❌ Chat Auth: Falha na renovação');
          return response;
        }

        attempt++;
        continue;
      }

      return response;

    } catch (error) {
      console.error('❌ Chat Fetch error:', error);
      throw error;
    }
  }

  throw new Error('Chat fetch failed after retries');
}

/**
 * ✅ NOVO: Criar instância Axios com retry automático (para APIs REST)
 * Isolado - não afeta axios global
 * 
 * @param {Function} refreshAccessToken - Função de refresh do AuthContext
 * @returns {AxiosInstance} Instância configurada
 */
export function createAxiosWithAuth(refreshAccessToken) {
  // Criar instância isolada
  const instance = axios.create({
    baseURL: process.env.NODE_ENV === 'development' 
      ? 'http://localhost:5000' 
      : window.location.origin,
    timeout: 30000
  });

  // Flag para evitar múltiplos refreshes simultâneos
  let isRetrying = false;

  // Request interceptor - adicionar token
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - retry automático em 401/403
  instance.interceptors.response.use(
    (response) => {
      // Sucesso - retornar response
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Se não for erro de auth, rejeitar imediatamente
      if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
        return Promise.reject(error);
      }

      // Se já tentou renovar, não tentar novamente
      if (originalRequest._retry) {
        console.error('❌ Chat Auth (Axios): Já tentou renovar, abortando');
        return Promise.reject(error);
      }

      // Se já está tentando renovar, aguardar
      if (isRetrying) {
        console.log('🔄 Chat Auth (Axios): Aguardando refresh em andamento...');
        
        // Aguardar refresh terminar
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!isRetrying) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });

        // Retentar com novo token
        const newToken = localStorage.getItem('token');
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        }
        
        return Promise.reject(error);
      }

      // Marcar que está tentando
      originalRequest._retry = true;
      isRetrying = true;

      try {
        console.warn('⚠️ Chat Auth (Axios): Token expirado, renovando...');

        // Tentar renovar token
        const refreshed = await refreshTokenForChat(refreshAccessToken);

        if (refreshed) {
          // Token renovado - retentar requisição original
          const newToken = localStorage.getItem('token');
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            console.log('✅ Chat Auth (Axios): Retentando requisição com novo token');
            return instance(originalRequest);
          }
        }

        console.error('❌ Chat Auth (Axios): Falha ao renovar token');
        return Promise.reject(error);

      } catch (refreshError) {
        console.error('❌ Chat Auth (Axios): Erro ao renovar:', refreshError);
        return Promise.reject(error);
      } finally {
        isRetrying = false;
      }
    }
  );

  return instance;
}

/**
 * ✅ Timer de refresh preventivo (a cada 20 minutos)
 */
export function startPreventiveRefresh(refreshAccessToken) {
  console.log('⏰ Chat Auth: Iniciando refresh preventivo');
  
  const intervalId = setInterval(() => {
    console.log('⏰ Chat Auth: Executando refresh preventivo...');
    
    refreshTokenForChat(refreshAccessToken)
      .then(success => {
        if (success) {
          console.log('✅ Chat Auth: Refresh preventivo concluído');
        }
      })
      .catch(err => {
        console.error('❌ Chat Auth: Erro no refresh preventivo:', err);
      });
      
  }, 20 * 60 * 1000);

  return () => {
    console.log('🛑 Chat Auth: Parando refresh preventivo');
    clearInterval(intervalId);
  };
}